import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '30vh',
  minHeight: '250px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  py: theme.spacing(6),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const termsStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms and Conditions - Thrana',
  url: 'https://www.thranatech.com/terms',
  description: 'Terms and Conditions for Thrana Softech Pvt. Ltd. governing access and use of our website and services.',
};

const Terms = () => {
  return (
    <>
      <SEO
        title="Terms and Conditions | Thrana"
        description="Terms and Conditions for Thrana Softech Pvt. Ltd. governing access and use of our website and services."
        path="/terms"
        structuredData={termsStructuredData}
      />
      <HeroSection>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              color: 'white',
            }}
          >
            Last Updated: 20-01-2026
          </Typography>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Typography variant="body1" style={{marginTop:'20px'}} paragraph sx={{ mb: 4 }}>
            These Terms and Conditions ("Terms") govern your access to and use of{' '}
            <a href="https://thranatech.com" style={{ color: '#3c005a' }}>https://thranatech.com</a> and related services provided by <strong>Thrana Softech Pvt. Ltd.</strong>
          </Typography>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing this website, you agree to be bound by these Terms. If you do not agree, you must not use this website.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              2. Use of Website
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Use the website only for lawful, business-related purposes</Typography></li>
              <li><Typography variant="body1">Not attempt to gain unauthorized access to systems or data</Typography></li>
              <li><Typography variant="body1">Not introduce malware, viruses, or harmful code</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              Unauthorized use may result in legal action.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              3. Intellectual Property Rights
            </Typography>
            <Typography variant="body1" paragraph>
              All content on this website, including:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Text, graphics, logos, icons</Typography></li>
              <li><Typography variant="body1">Software descriptions, product names, and trademarks</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              are the intellectual property of Thrana or its licensors and are protected under applicable intellectual property laws.
            </Typography>
            <Typography variant="body1" paragraph>
              No content may be copied, reproduced, or distributed without prior written consent.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              4. Product & Service Information
            </Typography>
            <Typography variant="body1" paragraph>
              Information on this website is provided for general informational purposes only.<br />
              Detailed functionality, pricing, SLAs, validation scope, and compliance commitments are governed solely by formal written agreements.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              5. Confidentiality
            </Typography>
            <Typography variant="body1" paragraph>
              Any non-public information exchanged during inquiries, demos, or discussions shall be treated as confidential and may be subject to separate NDA agreements.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              6. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the maximum extent permitted by law:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Thrana shall not be liable for any indirect, incidental, or consequential damages</Typography></li>
              <li><Typography variant="body1">Website content is provided <strong>"as is"</strong> without warranties of any kind</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              Use of the website is at your own risk.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              7. Indemnification
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to indemnify and hold harmless Thrana from claims arising out of:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Misuse of the website</Typography></li>
              <li><Typography variant="body1">Violation of these Terms</Typography></li>
              <li><Typography variant="body1">Infringement of third-party rights</Typography></li>
            </Box>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              8. Governing Law and Jurisdiction
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms shall be governed by and construed in accordance with the laws of India.<br />
              Courts located in <strong>Bengaluru, Karnataka</strong> shall have exclusive jurisdiction.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              9. Modifications
            </Typography>
            <Typography variant="body1" paragraph>
              Thrana reserves the right to modify these Terms at any time. Continued use of the website constitutes acceptance of updated Terms.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              10. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              For questions regarding these Terms:
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" paragraph>
                <strong>Thrana Softech Pvt. Ltd.</strong><br />
                <strong>Email:</strong> contact@thranatech.com<br />
                <strong>Website:</strong> <a href="https://thranatech.com" style={{ color: '#3c005a' }}>https://thranatech.com</a>
              </Typography>
            </Box>
          </Section>
        </Container>
      </ContentSection>
    </>
  );
};

export default Terms;
